import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';

import { BarMelding, BarMeldingType } from '@lvflanders/bar-ng';

import { NG_UI_STORAGE_KEY_PREFIX } from '../../constants';
import { BaasMelding } from '../baas-melding/baas-melding';

const READ_MELDINGEN_KEY = `${NG_UI_STORAGE_KEY_PREFIX}readMeldingen`;

@Component({
    selector: 'baas-meldingen',
    templateUrl: './baas-meldingen.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BaasMelding]
})
/** Represents a single opmerking */
export class BaasMeldingen {
    readonly meldingen = input.required<BarMelding[]>();

    protected readonly initialized = signal(false);
    protected readonly meldingenCollapsed = signal(false);
    protected readonly readMeldingen = signal<BarMelding[]>([]);
    protected readonly unreadMeldingen = signal<BarMelding[]>([]);
    protected readonly toggleMessage = computed(() => {
        if (this.meldingenCollapsed()) {
            return 'Open meldingen';
        } else {
            return 'Sluit meldingen';
        }
    });
    protected readonly toggleIconClass = computed(() => {
        if (this.meldingenCollapsed()) {
            return 'fa-solid fa-sm fa-chevron-right';
        } else {
            return 'fa-solid fa-sm fa-chevron-down';
        }
    });

    protected readonly numberOfOpgelost = computed(() => {
        return this.meldingen() ? this.meldingen().filter((_) => _.meldingType === BarMeldingType.Opgelost).length : undefined;
    });
    protected readonly numberOfBelangrijk = computed(() => {
        return this.meldingen() ? this.meldingen().filter((_) => _.meldingType === BarMeldingType.Belangrijk).length : undefined;
    });
    protected readonly numberOfInformatief = computed(() => {
        return this.meldingen() ? this.meldingen().filter((_) => _.meldingType === BarMeldingType.Informatief).length : undefined;
    });

    constructor() {
        effect(async () => {
            // Init readMeldingen
            const cachedMeldingen = await this.#getCachedMeldingIds();

            //Remove stale meldingen and set readMeldingen
            const meldingenToKeep = [] as BarMelding[];
            cachedMeldingen.forEach((cachedMeldingId) => {
                const melding = this.meldingen().find((mld) => mld.id === cachedMeldingId);
                if (melding) meldingenToKeep.push(melding);
            });
            await this.#saveMeldingenToKeep(meldingenToKeep);

            this.readMeldingen.set(meldingenToKeep);

            // Check if meldingen should be displayed
            this.unreadMeldingen.set(
                this.meldingen().filter(
                    (_) =>
                        !this.readMeldingen()
                            .map((mld) => mld.id)
                            .includes(_.id)
                )
            );
            if (!this.unreadMeldingen().length) this.meldingenCollapsed.set(true);
            this.initialized.set(true);
        });
    }

    protected toggleMeldingen() {
        if (this.meldingenCollapsed()) {
            this.#openMeldingen();
        } else {
            this.#closeMeldingen();
        }
    }

    async #closeMeldingen() {
        this.meldingenCollapsed.set(true);
        // Mark current unreadMeldingen as Read
        this.readMeldingen.set([...this.readMeldingen(), ...this.unreadMeldingen()]);
        this.unreadMeldingen.set([]);
        await this.#saveMeldingenToKeep(this.readMeldingen());
    }

    #openMeldingen() {
        this.meldingenCollapsed.set(false);
    }

    // NOTE use indexed db cache storage again when localforage has been replaced because there was an issue with this system
    // see TFS task #314157
    #getCachedMeldingIds(): Promise<number[]> {
        let result: number[];
        // Init readMeldingen
        const strCachedMeldingen = window.localStorage.getItem(READ_MELDINGEN_KEY);
        if (strCachedMeldingen) {
            result = JSON.parse(strCachedMeldingen);
        } else result = [];
        return Promise.resolve(result);
    }

    #saveMeldingenToKeep(meldingenToKeep: BarMelding[]): Promise<void> {
        window.localStorage.setItem(READ_MELDINGEN_KEY, JSON.stringify(meldingenToKeep.map((_) => _.id)));
        return Promise.resolve();
    }
}
